import { getErrorMessage } from "../utils/errorMessage";
import React, { useState, useEffect } from "react";
import api, { roleAPI, analyticsAPI } from "../api/client";
import { toast } from "react-toastify";
import "./admin.css";
import { useNavigate, Link, useLocation } from "react-router-dom";
import LoadingButton from "../components/LoadingButton";
import ConfirmModal from "../components/ConfirmModal";
import AccessManagement from "./AccessManagement";
import AnalyticsDashboard from "./AnalyticsDashboard";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";


export default function Admin({ initialTab } = {}) {
  const location = useLocation();
  // Sudo tabs: Users | User Access Control | Role Management | Analytics.
  // /admin/analytics deep-links into the Analytics tab AFTER the existing
  // Sudo auth below passes (logged-out users still see the Sudo Login first).
  // /admin keeps the existing default tab. No auth flow changes.
  const tabFromRoute = (pathname) =>
    String(pathname || "").startsWith("/admin/analytics") ? "analytics" : null;
  const [tab, setTab] = useState(
    tabFromRoute(location.pathname) || initialTab || "users"
  );
  /* ================= ADMIN AUTH ================== */
  const [loggedIn, setLoggedIn] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  // Purely visual: flips the password field's type attribute. Does not affect
  // the value, the payload sent to /admin/login, or validation.
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate(); 
  /* ================= FORM STATES ================= */
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [roleOptions, setRoleOptions] = useState([]);
  const [department, setDepartment] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  /* ================= DATA ================= */
  const [users, setUsers] = useState([]);

  /* ================= UI STATES ================= */
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToToggle, setUserToToggle] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  /* ============ USERS PAGINATION (25 per page) ============ */
  const USERS_PER_PAGE = 25;
  const [usersPage, setUsersPage] = useState(1);
  /* ===== USERS TAB: Add/Edit User modal + client-side search (UI only) ===== */
  const [showUserForm, setShowUserForm] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  // Existing RBAC gate for the Analytics tab: Sudo login alone never bypasses
  // the existing `analytics.view` permission. Resolved via the existing
  // /api/analytics/status endpoint (Sudo JWT + RBAC on the backend): ALLOW
  // renders the unchanged dashboard, DENY/401/403 renders a denial.
  const [analyticsState, setAnalyticsState] = useState("idle"); // idle|checking|allowed|denied
  const [analyticsError, setAnalyticsError] = useState("");

  // Keep the tab in sync with the URL so /admin/analytics always opens the
  // Analytics tab and /admin keeps the existing default tab.
  useEffect(() => {
    const routed = tabFromRoute(location.pathname);
    if (routed) setTab(routed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  /* ================= INIT ================= */
  useEffect(() => {
    const admin = localStorage.getItem("adminLoggedIn");
    if (admin === "true") {
      setLoggedIn(true);
      fetchUsers();
      fetchRoles();
    }
  }, []);

  /* ================= FETCH ROLE OPTIONS ================= */
  // Dynamic role list for the Add/Edit User dropdown. Loads ALL active roles
  // (system + custom) from the roles table via the existing role API — the
  // dropdown is NOT a hardcoded 6-role list. Disabled roles are excluded so
  // they cannot be newly assigned; an already-assigned disabled role stays
  // visible while editing (handled in the select below) so existing
  // assignments are preserved.
  const fetchRoles = async () => {
    try {
      const res = await roleAPI.getRoles();
      const roles = Array.isArray(res.data) ? res.data : [];
      setRoleOptions(roles.filter((r) => Number(r.is_active) === 1));
    } catch (err) {
      console.error("Fetch roles failed", err);
    }
  };

  // Refresh the role dropdown every time the User Management tab is opened so
  // roles created/edited in Role Management appear immediately.
  useEffect(() => {
    if (tab === "users" && loggedIn) fetchRoles();
  }, [tab, loggedIn]);

  // Resolve the existing analytics.view permission for the Sudo session before
  // mounting the dashboard, so DENY never flashes dashboard content.
  useEffect(() => {
    if (!loggedIn || tab !== "analytics") return;
    let cancelled = false;
    setAnalyticsState("checking");
    setAnalyticsError("");
    analyticsAPI.getStatus().then(
      () => { if (!cancelled) setAnalyticsState("allowed"); },
      (err) => {
        if (cancelled) return;
        setAnalyticsState("denied");
        setAnalyticsError(
          err?.response?.status === 403
            ? "Access denied: analytics.view is DENY for this Sudo session."
            : err?.response?.data?.message || "Analytics is not available for this Sudo session."
        );
      }
    );
    return () => { cancelled = true; };
  }, [tab, loggedIn]);

  /* ================= ADMIN LOGIN ================= */
  const handleAdminLogin = async () => {
    if (!username || !password) {
      setError("Enter username & password");
      return;
    }

    setLoading(true);
    setError("");
    setNotice("");

    try {
      const res = await api.post("/admin/login", { username, password });
      const token = res.data?.token;

      localStorage.setItem("adminLoggedIn", "true");
      if (token) {
        localStorage.setItem("adminToken", token);
      }
      setLoggedIn(true);
      fetchUsers();
      fetchRoles();
    } catch (err) {
      const data = err.response?.data;
      setError(data?.error || data?.message || "Invalid admin credentials");
    } finally {
      setLoading(false);
    }
  };

  /* ================= LOGOUT ================= */
  const handleLogout = () => {
  localStorage.removeItem("adminLoggedIn");
  localStorage.removeItem("adminToken");
  setLoggedIn(false);
  setUsers([]);
  resetForm();
  navigate("/login"); // ✅ redirect to login page
};

  /* ================= FETCH USERS ================= */
  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const token = localStorage.getItem("adminToken");
      const res = await api.get("/admin/users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(res.data);
    } catch (err) {
      console.error("Fetch users failed", err);
    } finally {
      setUsersLoading(false);
    }
  };

  /* ================= ADD USER ================= */
  const handleAddUser = async () => {
    if (!email || !name) {
      setError("Name and email are required.");
      return;
    }

    setActionLoading("add");
    setError("");
    setNotice("");
    try {
      await api.post("/admin/add-user", { name, email, role: role || null, department }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` },
      });
      setNotice("User added successfully");
      toast.success("User added successfully");
      await fetchUsers();
      await fetchRoles();
      resetForm();
      return true;
    } catch (err) {
      setError(getErrorMessage(err, "Failed to add user"));
      return false;
    } finally {
      setActionLoading("");
    }
  };

  /* ================= EDIT USER ================= */
  const handleEdit = (user) => {
    setEditingId(user.id);
    setEditingName(user.name);
    setName(user.name);
    setEmail(user.email);
    setRole(user.role || "");
    setDepartment(user.department || "");
    setError("");
    setNotice("");
    setShowUserForm(true);
  };

  /* ================= UPDATE USER ================= */
  const handleUpdateUser = async () => {
    setActionLoading("update");
    setError("");
    setNotice("");
    try {
      await api.put(`/admin/update-user/${editingId}`, {
        name,
        email,
        role: role || null,
        department,
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` },
      });
      setNotice("User updated");
      toast.success("User updated");
      await fetchUsers();
      await fetchRoles();
      resetForm();
      return true;
    } catch (err) {
      setError(getErrorMessage(err, "Update failed"));
      return false;
    } finally {
      setActionLoading("");
    }
  };

  /* ================= DELETE USER ================= */
  const handleDelete = (id) => {
    if (deletingId) return;
    const u = users.find((x) => x.id === id);
    setUserToDelete(u || { id });
  };

  const confirmDelete = async () => {
    if (!userToDelete || deletingId) return;
    const id = userToDelete.id;
    try {
      setDeletingId(id);
      setError("");
      setNotice("");
      await api.delete(`/admin/delete-user/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` },
      });
      setNotice("User deleted");
      setUserToDelete(null);
      await fetchUsers();
    } catch (err) {
      setError(getErrorMessage(err, "Delete failed"));
    } finally {
      setDeletingId(null);
    }
  };

  /* ================= TOGGLE USER ACTIVE ================= */
  const handleToggleActive = (user) => {
    if (actionLoading) return;
    setUserToToggle(user);
  };

  const confirmToggleActive = async () => {
    if (!userToToggle || actionLoading) return;
    const user = userToToggle;
    const nextActive = !(Number(user.is_active) === 1);
    const action = nextActive ? "enable" : "disable";
    try {
      setActionLoading(action);
      setError("");
      setNotice("");
      await api.patch(`/admin/toggle-user/${user.id}`, {
        isActive: nextActive ? 1 : 0,
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` },
      });
      setNotice(nextActive ? "User enabled" : "User disabled");
      setUserToToggle(null);
      await fetchUsers();
    } catch (err) {
      setError(getErrorMessage(err, "Failed to update user status"));
    } finally {
      setActionLoading("");
    }
  };

  /* ================= PAGINATION (UI only — 25 users/page) ============ */
  const searchQuery = userSearch.trim().toLowerCase();
  const filteredUsers = searchQuery
    ? users.filter((u) =>
        [u.name, u.email, u.role, u.department].some((v) =>
          String(v || "").toLowerCase().includes(searchQuery)
        )
      )
    : users;
  const usersTotalPages = Math.max(1, Math.ceil(filteredUsers.length / USERS_PER_PAGE));
  const usersSafePage = Math.min(usersPage, usersTotalPages);
  const pagedUsers = filteredUsers.slice(
    (usersSafePage - 1) * USERS_PER_PAGE,
    usersSafePage * USERS_PER_PAGE
  );

  /* ================= RESET FORM ================= */
  const resetForm = () => {
    setEditingId(null);
    setEditingName("");
    setName("");
    setEmail("");
    setRole("");
    setDepartment("");
  };

  /* ================= USER MODAL HELPERS ================= */
  const openAddUserModal = () => {
    resetForm();
    setError("");
    setNotice("");
    setShowUserForm(true);
  };

  const closeUserModal = () => {
    resetForm();
    setError("");
    setNotice("");
    setShowUserForm(false);
  };

  // Same save flow as before (handleAddUser / handleUpdateUser); the modal
  // closes only when the save succeeds and stays open on validation errors.
  const submitUserModal = async () => {
    const ok = editingId ? await handleUpdateUser() : await handleAddUser();
    if (ok) setShowUserForm(false);
  };

  /* ================= UI ================= */
  return (
    <div className={loggedIn ? "admin-container" : "admin-container admin-container--auth"}>
      {!loggedIn ? (
        /* ================= LOGIN SCREEN ================= */
        <div className="admin-auth">
          <div className="admin-auth-bg" aria-hidden="true">
            <span className="admin-auth-glow admin-auth-glow--teal" />
            <span className="admin-auth-glow admin-auth-glow--blue" />
            <span className="admin-auth-arc admin-auth-arc--a" />
            <span className="admin-auth-arc admin-auth-arc--b" />
            <span className="admin-auth-dots admin-auth-dots--tr" />
            <span className="admin-auth-dots admin-auth-dots--bl" />
          </div>

          <div className="admin-auth-content">
            <header className="admin-auth-brand">
              <img loading="lazy"
                src={require("../assest/login-brand.png")}
                alt="Univision Technology Solutions"
                className="admin-auth-logo"
              />
              <h1 className="admin-auth-title">Sudo Access</h1>
              <p className="admin-auth-subtitle">
                Privileged administrative access
              </p>
            </header>

            <section className="admin-auth-card">
              {error && (
                <p className="admin-auth-alert" role="alert">
                  <span className="admin-auth-alert-icon" aria-hidden="true">!</span>
                  <span>{error}</span>
                </p>
              )}

              <div className="admin-field">
                <label className="admin-field-label" htmlFor="sudo-username">
                  Username
                </label>
                <div className="admin-field-wrap">
                  <PersonOutlinedIcon className="admin-field-icon" fontSize="small" />
                  <input
                    id="sudo-username"
                    className="admin-field-input"
                    type="text"
                    placeholder="Enter username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                  />
                </div>
              </div>

              <div className="admin-field">
                <label className="admin-field-label" htmlFor="sudo-password">
                  Password
                </label>
                <div className="admin-field-wrap">
                  <LockOutlinedIcon className="admin-field-icon" fontSize="small" />
                  <input
                    id="sudo-password"
                    className="admin-field-input admin-field-input--has-toggle"
                    type={showPassword ? "text" : "password"}
                    placeholder=" Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAdminLogin()}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="admin-field-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon fontSize="small" />
                    ) : (
                      <VisibilityOutlinedIcon fontSize="small" />
                    )}
                  </button>
                </div>
              </div>

              <LoadingButton
                className="admin-auth-submit"
                onClick={handleAdminLogin}
                loading={loading}
                loadingText="Logging in..."
              >
                Secure Login
              </LoadingButton>

              <Link to="/login" className="admin-auth-back">
                <ArrowBackRoundedIcon fontSize="small" />
                <span>Back to Microsoft Sign In</span>
              </Link>
            </section>

            <p className="admin-auth-note">
              <LockOutlinedIcon fontSize="inherit" />
              <span>Restricted administrative access</span>
            </p>
          </div>
        </div>
      ) : (
        /* ================= DASHBOARD ================= */
        <div className="admin-dashboard">
          <style>{userFormCss}</style>
          <div className="dashboard-header">
            <h2>HR User Management</h2>
            <button className="logout-btn" onClick={handleLogout}>
              Logout
            </button>
          </div>

          {/* ================= AREA TABS ================= */}
          <div className="admin-tabs">
            <button
              type="button"
              className={tab === "users" ? "active" : ""}
              onClick={() => setTab("users")}
            >
              Users
            </button>
            <button
              type="button"
              className={tab === "uac" ? "active" : ""}
              onClick={() => setTab("uac")}
            >
              User Access Control
            </button>
            <button
              type="button"
              className={tab === "roles" ? "active" : ""}
              onClick={() => setTab("roles")}
            >
              Role Management
            </button>
            <button
              type="button"
              className={tab === "analytics" ? "active" : ""}
              onClick={() => setTab("analytics")}
            >
              Analytics
            </button>
          </div>

          {tab === "users" && (
          <>
          {/* ================= TABLE ================= */}
          <div className="table-section">
            <div className="users-table-toolbar">
              <h3>Users ({users.length})</h3>
              <button className="add-user-btn" onClick={openAddUserModal}>
                + Add User
              </button>
            </div>
            <div className="search-row">
              <input
                className="search-input"
                placeholder="Search by name, email, role, or department..."
                value={userSearch}
                onChange={(e) => {
                  setUserSearch(e.target.value);
                  setUsersPage(1);
                }}
              />
              {userSearch && (
                <button
                  className="search-clear"
                  title="Clear search"
                  onClick={() => {
                    setUserSearch("");
                    setUsersPage(1);
                  }}
                >
                  Clear
                </button>
              )}
            </div>

            <table className="hr-table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Role</th>
                  <th scope="col">Department</th>
                  <th scope="col">Status</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {usersLoading && (
                  <tr>
                    <td colSpan="6">Loading users...</td>
                  </tr>
                )}
                {pagedUsers.map((u) => (
                  <tr key={u.id} className={u.id === editingId ? "editing-row" : ""}>
                    <td><strong>{u.name}</strong></td>
                    <td><span className="cell-email">{u.email}</span></td>
                    <td>{u.role ? <span className="role-badge">{u.role}</span> : <span className="no-role">No Role</span>}</td>
                    <td>{u.department || "-"}</td>
                    <td>
                      <span
                        className={
                          Number(u.is_active) === 1 ? "status-pill active" : "status-pill inactive"
                        }
                      >
                        {Number(u.is_active) === 1 ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-row"
                          onClick={() => handleToggleActive(u)}
                          disabled={Boolean(actionLoading) || Boolean(deletingId)}
                        >
                          {actionLoading === (Number(u.is_active) === 1 ? "disable" : "enable")
                            ? "Working..."
                            : Number(u.is_active) === 1 ? "Disable" : "Enable"}
                        </button>
                        <button className="btn-row edit" onClick={() => handleEdit(u)} disabled={Boolean(deletingId) || Boolean(actionLoading)}>Edit</button>
                        <button className="btn-row danger" onClick={() => handleDelete(u.id)} disabled={deletingId === u.id || Boolean(actionLoading)}>
                          {deletingId === u.id ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!usersLoading && users.length === 0 && (
                  <tr>
                    <td colSpan="6">No users found</td>
                  </tr>
                )}
              </tbody>
            </table>
            {filteredUsers.length > USERS_PER_PAGE && (
              <div className="table-pagination">
                <button
                  onClick={() => setUsersPage(usersSafePage - 1)}
                  disabled={usersSafePage <= 1}
                >
                  ‹ Prev
                </button>
                <span className="page-info">
                  Page {usersSafePage} of {usersTotalPages} ({filteredUsers.length} users)
                </span>
                <button
                  onClick={() => setUsersPage(usersSafePage + 1)}
                  disabled={usersSafePage >= usersTotalPages}
                >
                  Next ›
                </button>
              </div>
            )}
          </div>
          {showUserForm && (
            <div className="um-overlay">
              <div className="um-modal">
                <div className="um-header">
                  <div className="um-title">
                    <h3>{editingId ? `Edit User: ${editingName}` : "Add HR User"}</h3>
                    <p>
                      {editingId
                        ? "Update the employee's details and role"
                        : "Create an HR portal user and assign a role"}
                    </p>
                  </div>
                  <button className="um-close" onClick={closeUserModal}>
                    x
                  </button>
                </div>
                <div className="um-body">
                  {error && <p className="um-error">{error}</p>}
                  {notice && <p className="um-notice">{notice}</p>}
                  <div className="um-grid">
                    <div className="field">
                      <label>Full Name</label>
                      <input
                        placeholder="e.g. Priya Sharma"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>
                    <div className="field">
                      <label>Email</label>
                      <input
                        type="email"
                        placeholder="name@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                    <div className="field">
                      <label>Role</label>
                      <select
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        title="Roles are managed in the Role Management tab"
                      >
                        <option value="">No Role (deny by default)</option>
                        {roleOptions.map((r) => (
                          <option key={r.id} value={r.name}>{r.name}</option>
                        ))}
                        {editingId && role && !roleOptions.some((r) => r.name === role) ? (
                          <option value={role}>{role} (current role)</option>
                        ) : null}
                      </select>
                    </div>
                    <div className="field">
                      <label>Department</label>
                      <input
                        placeholder="e.g. IT"
                        value={department}
                        onChange={(e) => setDepartment(e.target.value)}
                      />
                    </div>
                  </div>
                  <p className="um-hint">
                    Roles are managed in the Role Management tab. A user with No Role
                    starts fully denied and gets access only through User Access
                    Control grants. Role changes apply immediately after save.
                  </p>
                </div>
                <div className="um-footer">
                  <button className="um-cancel" onClick={closeUserModal} disabled={Boolean(actionLoading)}>
                    Cancel
                  </button>
                  <LoadingButton
                    onClick={submitUserModal}
                    loading={actionLoading === "add" || actionLoading === "update"}
                    loadingText={editingId ? "Updating..." : "Adding..."}
                  >
                    {editingId ? "Update" : "Add User"}
                  </LoadingButton>
                </div>
              </div>
            </div>
          )}
          </>
          )}

          {tab === "uac" && <AccessManagement view="uac" />}
          {tab === "roles" && <AccessManagement view="roles" />}
          {tab === "analytics" && (
            analyticsState === "allowed" ? (
              <AnalyticsDashboard />
            ) : (
              <div className="table-section">
                <div className="users-table-toolbar">
                  <h3>Analytics</h3>
                </div>
                <p className="um-hint">
                  {analyticsState === "checking" || analyticsState === "idle"
                    ? "Checking analytics access for this Sudo session…"
                    : analyticsError || "Access denied: analytics.view is DENY for this Sudo session."}
                </p>
              </div>
            )
          )}
        </div>
      )}
      <ConfirmModal
        isOpen={!!userToDelete}
        variant="danger"
        title="Delete user?"
        message="Delete this user?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={!!deletingId}
        loadingText="Deleting..."
        onConfirm={confirmDelete}
        onCancel={() => setUserToDelete(null)}
      />
      <ConfirmModal
        isOpen={!!userToToggle}
        variant={userToToggle && !(Number(userToToggle.is_active) === 1) ? "info" : "warning"}
        title={userToToggle ? `${!(Number(userToToggle.is_active) === 1) ? "Enable" : "Disable"} access?` : ""}
        message={userToToggle ? `${!(Number(userToToggle.is_active) === 1) ? "Enable" : "Disable"} access for ${userToToggle.name}?` : ""}
        confirmLabel={userToToggle && !(Number(userToToggle.is_active) === 1) ? "Enable" : "Disable"}
        cancelLabel="Cancel"
        isLoading={!!actionLoading}
        loadingText="Updating..."
        onConfirm={confirmToggleActive}
        onCancel={() => setUserToToggle(null)}
      />
    </div>
  );
}


const userFormCss = `

/* ===== Users tab: Add/Edit User modal + toolbar (same visual pattern as rm-/uac- modals) ===== */
.users-table-toolbar { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 14px; flex-wrap: wrap; }
.users-table-toolbar h3 { margin: 0; color: #172B4D; }
/* Status pills: previously inline-styled light green/red, which read as bright
   patches on the dark table. Now themed translucent badges. */
.status-pill { display: inline-block; padding: 3px 11px; border-radius: 999px; font-size: 12px; font-weight: 600; border: 1px solid transparent; }
.status-pill.active { color: #059669; background: #ECFDF5; border-color: #A7F3D0; }
.status-pill.inactive { color: #DC2626; background: #FEF2F2; border-color: #FECACA; }
.add-user-btn { padding: 9px 16px; background: linear-gradient(135deg, #0EA5E9 0%, #2563EB 100%); color: white; border: none; border-radius: 10px; font-weight: 600; font-size: 13px; cursor: pointer; font-family: var(--font-sans); transition: filter 0.15s ease; white-space: nowrap; }
.add-user-btn:hover { filter: brightness(1.1); }
.search-row { display: flex; align-items: center; gap: 8px; margin-bottom: 14px; }
.search-row .search-input { flex: 1; min-width: 220px; }
.search-clear { padding: 9px 14px; background: #F1F5F9; color: #475569; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 12px; font-weight: 700; cursor: pointer; font-family: var(--font-sans); }
.search-clear:hover { background: #E2E8F0; }
.um-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45); display: flex; align-items: center; justify-content: center; z-index: 1200; padding: 20px; }
.um-modal { background: #FFFFFF; color: #172B4D; border: 1px solid #E2E8F0; border-radius: 16px; width: 100%; max-width: 720px; max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 20px 60px rgba(15, 23, 42, 0.15); }
.um-header { display: flex; justify-content: space-between; align-items: center; padding: 18px 22px; border-bottom: 1px solid #E2E8F0; }
.um-title h3 { margin: 0 0 4px 0; font-size: 18px; color: #172B4D; }
.um-title p { margin: 0; font-size: 13px; color: #64748B; }
.um-close { background: #F1F5F9; color: #475569; border: 1px solid #E2E8F0; border-radius: 8px; width: 32px; height: 32px; font-size: 16px; cursor: pointer; font-weight: 700; flex-shrink: 0; }
.um-close:hover { background: #E2E8F0; }
.um-body { overflow-y: auto; padding: 18px 22px; display: grid; gap: 14px; }
.um-error { color: #FCA5A5; font-size: 13px; margin: 0; }
.um-notice { color: #6EE7B7; font-size: 13px; margin: 0; }
.um-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.um-grid .field { display: flex; flex-direction: column; gap: 6px; }
.um-grid .field label { font-size: 11px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; color: #94A3B8; }
.um-grid input, .um-grid select { padding: 10px 12px; border: 1px solid #CBD5E1 !important; border-radius: 10px; font-size: 13px; font-family: var(--font-sans); background: #FFFFFF !important; color: #172B4D !important; }
.um-grid select option { background: #FFFFFF !important; color: #172B4D !important; }
.um-grid input:focus, .um-grid select:focus { outline: none; border-color: #2563EB !important; box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12); }
.um-hint { margin: 0; font-size: 11.5px; line-height: 1.5; color: #64748B; }
.um-footer { padding: 14px 22px; border-top: 1px solid #E2E8F0; display: flex; justify-content: flex-end; gap: 10px; }
.um-cancel { padding: 9px 16px; background: #FFFFFF; color: #2563EB; border: 1px solid #CBD5E1; border-radius: 10px; font-weight: 600; font-size: 13px; cursor: pointer; font-family: var(--font-sans); }
.um-cancel:hover:not(:disabled) { background: #F8FAFC; }
.um-cancel:disabled { opacity: 0.5; cursor: not-allowed; }
@media (max-width: 640px) { .um-grid { grid-template-columns: 1fr; } }
`;
